import { useFeatureValue } from '@growthbook/growthbook-react';
import { ArrowDropDown } from '@mui/icons-material';
import { Box } from '@mui/material';
import { EntityState } from '@reduxjs/toolkit';
import * as R from 'ramda';
import { useState } from 'react';

import { Button } from '@/components';
import { EpisodeFilterDefaults, SortByOptions } from '@/constants';
import {
  LibraryCategoryEntity,
  selectSessionUserId,
  useAppSelector,
  useFetchEpisodeFacetsQuery,
} from '@/store';
import { LibraryFilters } from '@/types';
import { getFacetValueText } from '@/utils';

import { SelectedCategoryInfo } from '../CategoryTree';
import { ActiveFilter, CategoryModal, FacetModal } from './components';

interface Settings {
  filters: LibraryFilters | null;
  selectedCategoryInfo: SelectedCategoryInfo;
}

type MobileFiltersProps = {
  settings: Settings;
  onChange: (props: Settings) => void;
  onReset?: () => void;
  categories?: EntityState<LibraryCategoryEntity, string>;
};

export const MobileFilters = ({
  settings,
  onChange,
  onReset,
  categories,
}: MobileFiltersProps) => {
  const isAudienceFunctionEnabled = useFeatureValue(
    'audience-function-select',
    true,
  );

  const [activeFilter, setActiveFilter] = useState<ActiveFilter | null>(null);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const userId = useAppSelector(selectSessionUserId);

  // TODO: Make this a bit more dynamic and less hardcoded?
  const { data: facets } = useFetchEpisodeFacetsQuery();
  const mediaTypeFacet = facets?.entities['media_type'];
  const audienceFunctionFacet = facets?.entities['audience_function'];

  const selectedCategory = settings.selectedCategoryInfo.categorySlug
    ? categories?.entities[settings.selectedCategoryInfo.categorySlug]
    : null;
  const selectedSubCategory =
    selectedCategory && settings.selectedCategoryInfo.subCategorySlug
      ? selectedCategory.subCategories.entities[
          settings.selectedCategoryInfo.subCategorySlug
        ]
      : null;
  const categoryLabel = selectedSubCategory?.name
    ? `${selectedCategory?.name} - ${selectedSubCategory.name}`
    : selectedCategory?.name;

  if (!settings?.filters) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        overflowX: 'auto',
        mx: -2,
        px: 2,
        '> *': { flexShrink: 0 },
      }}>
      <Button
        variant="secondary"
        label={categoryLabel ? `Category: ${categoryLabel}` : 'Category'}
        colorOverride={categoryLabel ? 'secondary.main' : undefined}
        endIcon={<ArrowDropDown />}
        onClick={() => setIsCategoryModalOpen(true)}
        fullWidth={false}
      />
      {!!mediaTypeFacet && (
        <Button
          variant="secondary"
          label={getFacetValueText(
            mediaTypeFacet,
            !R.equals(settings.filters.mediaType, [
              EpisodeFilterDefaults.mediaType,
            ])
              ? settings.filters.mediaType
              : undefined,
          )}
          colorOverride={
            !R.equals(settings.filters.mediaType, [
              EpisodeFilterDefaults.mediaType,
            ])
              ? 'secondary.main'
              : undefined
          }
          endIcon={<ArrowDropDown />}
          onClick={() => setActiveFilter('mediaType')}
          fullWidth={false}
        />
      )}
      {!!userId && !!audienceFunctionFacet && isAudienceFunctionEnabled && (
        <Button
          variant="secondary"
          label={getFacetValueText(
            audienceFunctionFacet,
            settings.filters.audienceFunction,
          )}
          colorOverride={
            !R.equals(settings.filters.audienceFunction, [
              EpisodeFilterDefaults.audienceFunction,
            ])
              ? 'secondary.main'
              : undefined
          }
          endIcon={<ArrowDropDown />}
          onClick={() => setActiveFilter('audienceFunction')}
          fullWidth={false}
        />
      )}
      <Button
        variant="secondary"
        label={`Sort: ${SortByOptions[settings.filters.sortBy?.[0] as keyof typeof SortByOptions]?.text || settings.filters.sortBy?.[0]}`}
        colorOverride={
          settings.filters.sortBy &&
          settings.filters.sortBy[0] !== EpisodeFilterDefaults.sortBy
            ? 'secondary.main'
            : undefined
        }
        endIcon={<ArrowDropDown />}
        onClick={() => setActiveFilter('sortBy')}
        fullWidth={false}
      />
      <Button
        variant="secondary"
        label="Reset Filters"
        onClick={onReset}
        fullWidth={false}
        colorOverride={onReset ? 'secondary.main' : undefined}
      />
      <FacetModal
        activeFilter={activeFilter}
        currentValue={settings.filters[activeFilter!] ?? []}
        onChange={value => {
          if (!activeFilter) return;
          onChange({
            ...settings,
            filters: {
              ...settings.filters!,
              [activeFilter]: value,
            },
          });
        }}
        onClose={() => setActiveFilter(null)}
      />
      <CategoryModal
        isOpen={isCategoryModalOpen}
        onClose={() => setIsCategoryModalOpen(false)}
        categories={categories}
        selectedCategoryInfo={settings.selectedCategoryInfo}
        onChange={selectedCategoryInfo => {
          onChange({
            ...settings,
            selectedCategoryInfo,
          });
        }}
      />
    </Box>
  );
};
